import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#app/utils/misc.tsx'
import { Icon } from './icon.tsx'
import { Input } from './input.tsx'
import { Popover, PopoverContent, PopoverTrigger } from './popover.tsx'

/**
 * DatePicker + TimeField — a date picker and a *separate* time field, not a
 * combined mega-picker (the grounded handoff's deliberate split). The date
 * trigger mirrors the `Input` / `Select` metrics (same `--input` border,
 * `bg-background`, `h-8`, `focus-cosy` ring, and the `aria-invalid` →
 * `--input-invalid` contract) with a leading calendar glyph; it opens a calendar
 * panel on the shared `Popover` substrate (outside-click / `Esc` dismissal,
 * anchored motion). The panel is a 280px month grid: a header with ghost
 * prev/next icon buttons, a day-of-week row, and 32px day cells — hover paints
 * `--bg-hover`, today carries a `--gray-5` ring + weight, the selected day is a
 * `--brand` fill, and out-of-month / out-of-range days are disabled. Swapping
 * months cross-fades on the shared `month-swap` keyframe.
 *
 * The calendar is a `role="grid"` (rows / gridcells / columnheaders) with roving
 * focus: arrows move by day / week (crossing month boundaries follows the
 * focused day), `Enter`/`Space` selects, `Esc` closes. Month and weekday names
 * come from `Intl.DateTimeFormat` in the app's active locale (the
 * recipient-locale precedent). Controlled only — pass `value` + `onValueChange`.
 *
 * `TimeField` is the sibling: an `Input`-metric field with a leading clock glyph
 * over native `<input type="time">` semantics (the browser owns the spinners /
 * the dropdown). Tokens only.
 */

export type DatePickerProps = {
	/** Controlled selected day (`undefined` = nothing chosen). */
	value?: Date
	/** Fires with the newly chosen day (midnight, local). */
	onValueChange?: (value: Date) => void
	/** Earliest selectable day (inclusive); earlier days are disabled. */
	min?: Date
	/** Latest selectable day (inclusive); later days are disabled. */
	max?: Date
	/** Resting trigger label when nothing is chosen. @default t('datePicker.placeholder') */
	placeholder?: string
	/** Render with the panel initially open (uncontrolled) — for the specimen. @default false */
	defaultOpen?: boolean
	/** Disable the trigger. @default false */
	disabled?: boolean
	/** Marks the trigger invalid — repaints the border with `--input-invalid`. */
	'aria-invalid'?: boolean
	/** Accessible name for the trigger. */
	'aria-label'?: string
	'aria-labelledby'?: string
	id?: string
	className?: string
}

/** How many week rows the month grid always paints (a stable 6-row calendar). */
const WEEK_ROWS = 6
/** A known Monday, used to derive locale-short weekday labels for the dow row. */
const REFERENCE_MONDAY = new Date(2024, 0, 1)

/** Local `YYYY-MM-DD` key — the comparable identity for a calendar day. */
function isoDay(date: Date): string {
	const year = date.getFullYear()
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const day = String(date.getDate()).padStart(2, '0')
	return `${year}-${month}-${day}`
}

function addDays(date: Date, amount: number): Date {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount)
}

function startOfMonth(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), 1)
}

function addMonths(date: Date, amount: number): Date {
	return new Date(date.getFullYear(), date.getMonth() + amount, 1)
}

function isSameMonth(a: Date, b: Date): boolean {
	return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
}

/** True when `date` falls outside the inclusive `[min, max]` range (by day). */
function outOfRange(date: Date, min?: Date, max?: Date): boolean {
	const key = isoDay(date)
	if (min && key < isoDay(min)) return true
	if (max && key > isoDay(max)) return true
	return false
}

/** The Monday-started 6×7 grid of days covering `month`. */
function monthGrid(month: Date): Date[][] {
	const first = startOfMonth(month)
	// getDay: 0=Sun … 6=Sat; shift so Monday is column 0.
	const mondayOffset = (first.getDay() + 6) % 7
	const gridStart = addDays(first, -mondayOffset)
	return Array.from({ length: WEEK_ROWS }, (_, week) =>
		Array.from({ length: 7 }, (_, day) => addDays(gridStart, week * 7 + day)),
	)
}

function DatePicker({
	value,
	onValueChange,
	min,
	max,
	placeholder,
	defaultOpen = false,
	disabled = false,
	className,
	id,
	'aria-invalid': ariaInvalid,
	'aria-label': ariaLabel,
	'aria-labelledby': ariaLabelledby,
}: DatePickerProps) {
	const { t, i18n } = useTranslation()
	const locale = i18n.language || 'en'
	const today = new Date()

	const [open, setOpen] = React.useState(defaultOpen)
	// The month currently shown; seeded from the value (or today) each open.
	const [displayMonth, setDisplayMonth] = React.useState(() =>
		startOfMonth(value ?? today),
	)
	// The roving-focus day within the grid.
	const [focusedDate, setFocusedDate] = React.useState(() => value ?? today)
	// Set whenever focus should follow `focusedDate` into the grid (open +
	// keyboard nav), so a prev/next *click* never yanks focus off its button.
	const shouldFocusRef = React.useRef(false)
	const gridRef = React.useRef<HTMLDivElement>(null)

	const monthFormat = React.useMemo(
		() => new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }),
		[locale],
	)
	const dayLabelFormat = React.useMemo(
		() => new Intl.DateTimeFormat(locale, { dateStyle: 'full' }),
		[locale],
	)
	const triggerFormat = React.useMemo(
		() => new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }),
		[locale],
	)
	const weekdayLabels = React.useMemo(() => {
		const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' })
		return Array.from({ length: 7 }, (_, i) =>
			weekday.format(addDays(REFERENCE_MONDAY, i)),
		)
	}, [locale])

	function handleOpenChange(next: boolean) {
		setOpen(next)
		if (next) {
			// Reopen focused on the selection (or today), clamped into view.
			const anchor = value ?? today
			setDisplayMonth(startOfMonth(anchor))
			setFocusedDate(anchor)
			shouldFocusRef.current = true
		}
	}

	function goToMonth(next: Date) {
		shouldFocusRef.current = false
		setDisplayMonth(next)
	}

	function moveFocus(deltaDays: number) {
		const next = addDays(focusedDate, deltaDays)
		// Clamp: an out-of-range move is ignored, keeping focus in bounds.
		if (outOfRange(next, min, max)) return
		shouldFocusRef.current = true
		setFocusedDate(next)
		if (!isSameMonth(next, displayMonth)) setDisplayMonth(startOfMonth(next))
	}

	function handleSelect(day: Date) {
		onValueChange?.(day)
		setOpen(false)
	}

	/** Move DOM focus onto the day button carrying the roving `focusedDate`. */
	function focusRovingDay() {
		gridRef.current
			?.querySelector<HTMLButtonElement>(`[data-date="${isoDay(focusedDate)}"]`)
			?.focus()
	}

	function handleGridKeyDown(event: React.KeyboardEvent) {
		const deltas: Record<string, number> = {
			ArrowLeft: -1,
			ArrowRight: 1,
			ArrowUp: -7,
			ArrowDown: 7,
		}
		const delta = deltas[event.key]
		if (delta === undefined) return
		event.preventDefault()
		moveFocus(delta)
		// Enter/Space fall through to the native <button> activation → onClick.
	}

	// Move DOM focus onto the roving day whenever the grid asks for it.
	React.useLayoutEffect(() => {
		if (!open || !shouldFocusRef.current) return
		shouldFocusRef.current = false
		focusRovingDay()
		// eslint-disable-next-line react-hooks/exhaustive-deps -- focusRovingDay reads the latest focusedDate; run on these transitions only.
	}, [open, focusedDate, displayMonth])

	const selectedKey = value ? isoDay(value) : null
	const todayKey = isoDay(today)
	const focusedKey = isoDay(focusedDate)

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>
				<button
					type="button"
					aria-invalid={ariaInvalid}
					aria-label={ariaLabel}
					aria-labelledby={ariaLabelledby}
					id={id}
					disabled={disabled}
					data-slot="date-picker-trigger"
					className={cn(
						// Mirror the Input / Select trigger surface exactly.
						'focus-cosy border-input bg-background aria-[invalid]:border-input-invalid text-body-sm md:text-body-xs flex h-8 w-full items-center gap-2 rounded-md border px-3 py-1.5 outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
						className,
					)}
				>
					<Icon
						name="calendar"
						className="text-muted-foreground size-4 shrink-0"
						aria-hidden
					/>
					<span className={cn('truncate', !value && 'text-muted-foreground')}>
						{value
							? triggerFormat.format(value)
							: (placeholder ?? t('datePicker.placeholder'))}
					</span>
				</button>
			</PopoverTrigger>
			<PopoverContent
				align="start"
				data-slot="date-picker-content"
				// Name the calendar dialog after the field so it isn't an anonymous
				// `role="dialog"` (axe `aria-dialog-name`).
				aria-label={ariaLabel}
				aria-labelledby={ariaLabelledby}
				className="w-[280px] p-3"
				// Focus the roving day ourselves rather than the panel root (also
				// covers the defaultOpen specimen, where no open transition fires).
				onOpenAutoFocus={(event) => {
					event.preventDefault()
					focusRovingDay()
				}}
			>
				<div className="mb-2 flex items-center justify-between">
					<CalendarNavButton
						label={t('datePicker.previousMonth')}
						icon="arrow-left"
						onClick={() => goToMonth(addMonths(displayMonth, -1))}
					/>
					<span
						aria-live="polite"
						className="text-body-sm font-semibold tabular-nums"
					>
						{monthFormat.format(displayMonth)}
					</span>
					<CalendarNavButton
						label={t('datePicker.nextMonth')}
						icon="arrow-right"
						onClick={() => goToMonth(addMonths(displayMonth, 1))}
					/>
				</div>
				<div
					ref={gridRef}
					role="grid"
					aria-label={monthFormat.format(displayMonth)}
					onKeyDown={handleGridKeyDown}
				>
					<div role="row" className="grid grid-cols-7">
						{weekdayLabels.map((label, i) => (
							<div
								key={i}
								role="columnheader"
								aria-label={label}
								className="text-muted-foreground text-body-xs flex h-8 items-center justify-center font-normal"
							>
								{label}
							</div>
						))}
					</div>
					{/* Keyed on the month so a swap re-mounts and re-runs the
					    cross-fade; role=rowgroup keeps the grid structure intact. */}
					<div
						role="rowgroup"
						key={isoDay(displayMonth)}
						className="animate-month-swap"
					>
						{monthGrid(displayMonth).map((week, w) => (
							<div key={w} role="row" className="grid grid-cols-7">
								{week.map((day) => {
									const key = isoDay(day)
									const outside = !isSameMonth(day, displayMonth)
									const unavailable = outside || outOfRange(day, min, max)
									const selected = key === selectedKey
									const isToday = key === todayKey
									return (
										<div
											key={key}
											role="gridcell"
											className="flex justify-center p-0.5"
										>
											<button
												type="button"
												data-date={key}
												data-today={isToday || undefined}
												data-selected={selected || undefined}
												data-outside={outside || undefined}
												disabled={unavailable}
												tabIndex={key === focusedKey ? 0 : -1}
												aria-label={dayLabelFormat.format(day)}
												aria-pressed={selected}
												onClick={() => handleSelect(day)}
												className={cn(
													'focus-cosy text-body-sm flex size-8 items-center justify-center rounded-md tabular-nums outline-hidden',
													!unavailable &&
														!selected &&
														'hover:bg-[var(--bg-hover)]',
													selected &&
														'bg-brand text-primary-foreground hover:bg-brand',
													isToday &&
														!selected &&
														'font-semibold ring-1 ring-[var(--gray-5)] ring-inset',
													unavailable &&
														'text-muted-foreground pointer-events-none opacity-40',
												)}
											>
												{day.getDate()}
											</button>
										</div>
									)
								})}
							</div>
						))}
					</div>
				</div>
			</PopoverContent>
		</Popover>
	)
}

/** Ghost icon button for the month prev/next controls. */
function CalendarNavButton({
	label,
	icon,
	onClick,
}: {
	label: string
	icon: 'arrow-left' | 'arrow-right'
	onClick: () => void
}) {
	return (
		<button
			type="button"
			aria-label={label}
			onClick={onClick}
			className="focus-cosy text-muted-foreground hover:text-foreground inline-flex size-8 items-center justify-center rounded-md outline-hidden hover:bg-[var(--bg-hover)]"
		>
			<Icon name={icon} className="size-4" />
		</button>
	)
}

export type TimeFieldProps = {
	/** Controlled `HH:MM` value. */
	value?: string
	/** Fires with the new `HH:MM` string. */
	onValueChange?: (value: string) => void
	/** Earliest selectable time (`HH:MM`). */
	min?: string
	/** Latest selectable time (`HH:MM`). */
	max?: string
	/** Step in seconds (native `<input type="time">` semantics). */
	step?: number
	disabled?: boolean
	'aria-invalid'?: boolean
	'aria-label'?: string
	'aria-labelledby'?: string
	id?: string
	className?: string
}

/**
 * TimeField — the DatePicker's sibling: an `Input`-metric field with a leading
 * clock glyph over native `<input type="time">` semantics. The browser owns the
 * spinners / picker; we own only the chrome. Tokens only.
 */
function TimeField({
	value,
	onValueChange,
	min,
	max,
	step,
	disabled,
	className,
	id,
	'aria-invalid': ariaInvalid,
	'aria-label': ariaLabel,
	'aria-labelledby': ariaLabelledby,
}: TimeFieldProps) {
	return (
		<Input
			type="time"
			data-slot="time-field"
			leadingAdornment={<Icon name="clock" className="size-4" />}
			// Always controlled — `?? ''` keeps an unset field from starting
			// uncontrolled and flipping to controlled on first change.
			value={value ?? ''}
			onChange={(event) => onValueChange?.(event.target.value)}
			min={min}
			max={max}
			step={step}
			disabled={disabled}
			id={id}
			aria-invalid={ariaInvalid}
			aria-label={ariaLabel}
			aria-labelledby={ariaLabelledby}
			className={className}
		/>
	)
}

export { DatePicker, TimeField }
